import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'family' | 'alumni';
  familyId?: string;
  alumniId?: string;
  firstName: string;
  lastName: string;
  isSuperAdmin?: boolean;
  adminPermissions?: Array<{
    permissionKey: string;
    canRead: boolean;
  }>;
}

interface ImpersonationInfo {
  isImpersonating: boolean;
  adminEmail: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  mustChangePassword: boolean;
  onPasswordChanged: () => void;
  retryAuth: () => void;
  updateUserProfile: (firstName: string, lastName: string) => void;
  impersonation: ImpersonationInfo | null;
  startImpersonation: (userId: string) => Promise<boolean>;
  stopImpersonation: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [lastRetryTime, setLastRetryTime] = useState(0);
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null);

  useEffect(() => {
    // Check for existing auth token on app load
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    
    if (token && storedUser) {
      // Load user data from localStorage
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setMustChangePassword(userData.mustChangePassword || false);
        console.log('Auth token found, user data loaded from localStorage');
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        // Clear invalid data
        localStorage.removeItem('authUser');
      }
      setLoading(false);
    } else if (token) {
      // Token exists but no user data, verify to get it
      console.log('Auth token found, verifying to load user data');
      verifyToken(token);
    } else {
      setLoading(false);
    }

  }, []);

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle rate limiting specifically
      if (response.status === 429) {
        console.warn('Rate limited - too many verification requests. Keeping existing session.');
        setLoading(false);
        return;
      }

      // Only try to parse JSON if we expect it
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        setUser(null);
        setLoading(false);
        return;
      }

      if (response.ok && data.success && data.user) {
        setUser(data.user);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        setMustChangePassword(data.user.mustChangePassword || false);
      } else {
        // Token verification failed, but don't logout automatically
        console.log('Token verification failed, but keeping session:', data.message || 'Unknown error');
        setUser(null);
        localStorage.removeItem('authUser');
      }
    } catch (error) {
      console.error('Token verification failed, but keeping session:', error);
      setUser(null);
      localStorage.removeItem('authUser');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok && data.success && data.token) {
        // Store the real JWT token
        localStorage.setItem('authToken', data.token);
        
        // Store user data for access in components
        localStorage.setItem('authUser', JSON.stringify(data.user));
        
        // Set user data from API response
        setUser(data.user);
        
        // Check if password change is required
        setMustChangePassword(data.mustChangePassword || false);
        
        console.log(`✅ Login successful for: ${data.user.email}`);
        return true;
      } else {
        console.error('Login failed:', data.message);
        return false;
      }
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.setItem('loggedOut', 'true');
    setMustChangePassword(false);
  };



  const onPasswordChanged = () => {
    setMustChangePassword(false);
  };

  const retryAuth = () => {
    const token = localStorage.getItem('authToken');
    const now = Date.now();
    
    // Prevent retry if we've tried within the last 10 seconds
    if (now - lastRetryTime < 10000) {
      console.log('Retry cooldown active, skipping retry');
      return;
    }
    
    if (token && !user) {
      console.log('Retrying authentication...');
      setLastRetryTime(now);
      verifyToken(token);
    }
  };

  const updateUserProfile = (firstName: string, lastName: string) => {
    if (user) {
      const updatedUser = {
        ...user,
        firstName,
        lastName
      };
      setUser(updatedUser);
      localStorage.setItem('authUser', JSON.stringify(updatedUser));
      console.log('✅ User profile updated in context');
    }
  };

  const startImpersonation = async (userId: string): Promise<boolean> => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/impersonation/start/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success && data.token) {
        // Store the new impersonation token
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        
        setUser(data.user);
        setImpersonation(data.impersonation);
        setMustChangePassword(false);
        
        console.log(`🎭 Now impersonating: ${data.user.email}`);
        return true;
      } else {
        console.error('Impersonation failed:', data.message);
        return false;
      }
    } catch (error) {
      console.error('Impersonation failed:', error);
      return false;
    }
  };

  const stopImpersonation = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/impersonation/stop', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success && data.token) {
        // Restore admin token
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        
        setUser(data.user);
        setImpersonation(null);
        setMustChangePassword(false);
        
        console.log(`🎭 Returned to admin account: ${data.user.email}`);
        return true;
      } else {
        console.error('Stop impersonation failed:', data.message);
        return false;
      }
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      return false;
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    mustChangePassword,
    onPasswordChanged,
    retryAuth,
    updateUserProfile,
    impersonation,
    startImpersonation,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 